import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePortfolioProjects } from '@/hooks/usePortfolioProjects';

export interface PolicyBreach {
  policy_id: string;
  policy_name: string;
  ai_output_id: string;
  output_title: string;
  hours_pending: number;
}

// PMO-only data on top of useTodayData: budgets and freshness per project, and live policy breaches.
// Both are the caller's own tenant data (RLS-scoped). A failed query leaves its value null so the screen
// says "not recorded" instead of showing a zero that looks like good news.
export function usePmoData() {
  const { tenantId } = useAuth();

  const projects = usePortfolioProjects();

  const breaches = useQuery({
    queryKey: ['pmo', 'breaches', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<PolicyBreach[]> => {
      const { data, error } = await supabase.rpc('check_policy_breaches', { p_tenant_id: tenantId as string });
      if (error) throw new Error(error.message);
      return (data ?? []) as PolicyBreach[];
    },
  });

  return {
    projects: projects.data ?? [],
    /** null = could not be read (not the same as zero breaches). */
    breaches: breaches.isError || (!breaches.data && !breaches.isLoading) ? null : breaches.data ?? [],
    isLoading: projects.isLoading,
    projectsFailed: projects.isError,
  };
}
